import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import publicLapApi, { PublicApiError } from '../../clients/PublicLapClient';
import { RaceLap, TelemetryLapData } from '../../models/TelemetryData';
import { ReferenceLap } from '../../models/responseBodies/ReferenceLapsResponse';
import TelemetryCharts from '../telemetry/TelemetryCharts';
import { getLapColor, REF_LAP_ID } from '../telemetry/LapSelector';
import LogoLink from '../LogoLink';
import OfferBanner from './OfferBanner';
import CopyLinkButton from './CopyLinkButton';
import { computeSectorDistances } from '../../utils/sectorDistances';
import { TrackTurn } from '../../utils/turnMarkers';
import {
  bestValidLap,
  firstValidLap,
  formatLapDate,
  formatLapTime,
  formatSignedDelta,
  isCountedLap,
  shareableLapUrl,
  venueDisplayName,
} from '../../utils/publicLap';
import '../telemetry/TelemetryPage.css';
import './PublicLapPage.css';

type LoadState = 'loading' | 'ready' | 'notFound' | 'rateLimited' | 'error';
type CompareKey = 'first' | 'record' | 'none';

/** Speed + throttle/brake, as the mockup; delta whenever there's something to compare with. */
const channelsFor = (comparing: boolean): Record<string, boolean> => ({
  speed: true,
  throttle: true,
  brake: true,
  steering: false,
  rpm: false,
  gear: false,
  delta: comparing,
});

interface Sectored { sec1: number; sec2: number; sec3: number }

const sectorsOf = (lap: Sectored): number[] => [lap.sec1, lap.sec2, lap.sec3];

/**
 * The public, no-login view of one lap (spec D12, §12): the link in the post-session email.
 * Reuses the telemetry charts, fed from CommonApi's anonymous /public/racedata endpoints.
 */
const PublicLapPage: React.FC = () => {
  const { raceId, lapId } = useParams<{ raceId: string; lapId: string }>();
  const { search } = useLocation();
  const { user } = useAuth();
  const offerId = useMemo(() => new URLSearchParams(search).get('offer'), [search]);

  const [laps, setLaps] = useState<RaceLap[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [telemetry, setTelemetry] = useState<Map<string, TelemetryLapData>>(new Map());
  const [failedTelemetry, setFailedTelemetry] = useState<Set<string>>(new Set());
  const [recordLap, setRecordLap] = useState<ReferenceLap | null>(null);
  const [compareChoice, setCompareChoice] = useState<CompareKey | null>(null);
  const [turns, setTurns] = useState<TrackTurn[]>([]);

  const lapsRef = useRef<RaceLap[]>([]);
  lapsRef.current = laps;
  const requested = useRef(new Set<string>());
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // Laps for the session. Moving to another lap of the same session reuses the list.
  useEffect(() => {
    if (!lapId) return;
    if (lapsRef.current.some((l) => l.id === lapId)) {
      setLoadState('ready');
      return;
    }
    const controller = new AbortController();
    setLoadState('loading');
    publicLapApi.getLapsForLap(lapId, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (!Array.isArray(result) || !result.some((l) => l.id === lapId)) {
          setLaps([]);
          setLoadState('notFound');
          return;
        }
        setLaps(result);
        setLoadState('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const status = err instanceof PublicApiError ? err.status : 0;
        setLoadState(status === 404 ? 'notFound' : status === 429 ? 'rateLimited' : 'error');
      });
    return () => controller.abort();
  }, [lapId, reloadKey]);

  const ensureTelemetry = useCallback((id: string) => {
    if (requested.current.has(id)) return;
    requested.current.add(id);
    publicLapApi.getLapTelemetry(id)
      .then((data) => {
        if (mounted.current) setTelemetry((prev) => new Map(prev).set(id, data));
      })
      .catch(() => {
        if (mounted.current) setFailedTelemetry((prev) => new Set(prev).add(id));
      });
  }, []);

  const selectedLap = useMemo(() => laps.find((l) => l.id === lapId) ?? null, [laps, lapId]);
  const firstValid = useMemo(() => firstValidLap(laps), [laps]);
  const best = useMemo(() => bestValidLap(laps), [laps]);
  const firstCompare = firstValid && firstValid.id !== lapId ? firstValid : null;

  // The session's track/car/layout (game ids). One key, so moving between laps of the session
  // doesn't refetch the record or the turns.
  const comboKey = laps[0]
    ? JSON.stringify([laps[0].gameTrack ?? '', laps[0].gameCar ?? '', laps[0].gameLayout ?? ''])
    : '';

  // The WOR-wide lap record, public (anyone can compare with it). A failure just drops the option.
  useEffect(() => {
    setRecordLap(null);
    if (!comboKey) return;
    const [track, car, layout] = JSON.parse(comboKey) as [string, string, string];
    if (!track || !car) return;
    const controller = new AbortController();
    publicLapApi.getLapRecord(track, car, layout, controller.signal)
      .then((refs) => { if (!controller.signal.aborted) setRecordLap(refs?.lapRecord ?? null); })
      .catch(() => undefined);
    return () => controller.abort();
  }, [comboKey]);

  // Official turns for the layout; [] (not fully mapped) or a failure shows no turn labels.
  useEffect(() => {
    setTurns([]);
    if (!comboKey) return;
    const [track, , layout] = JSON.parse(comboKey) as [string, string, string];
    if (!track) return;
    const controller = new AbortController();
    publicLapApi.getCorners(track, layout, controller.signal)
      .then((result) => { if (!controller.signal.aborted) setTurns(Array.isArray(result) ? result : []); })
      .catch(() => undefined);
    return () => controller.abort();
  }, [comboKey]);
  const recordCompare = recordLap && recordLap.id !== lapId ? recordLap : null;

  // Default: the first valid lap (D8), else the record, else nothing. A choice that stops being
  // available (moving to the first valid lap itself) falls back the same way.
  const available: CompareKey[] = [
    ...(firstCompare ? ['first' as const] : []),
    ...(recordCompare ? ['record' as const] : []),
    'none',
  ];
  const compare: CompareKey = compareChoice && available.includes(compareChoice)
    ? compareChoice
    : available[0];

  // Memoised: the charts redraw whenever their labels change, so this must be stable per choice.
  const compareTarget = useMemo<{ id: string; label: string; legend: string; sectors: number[] } | null>(() => {
    if (compare === 'first' && firstCompare) {
      return {
        id: firstCompare.id,
        label: `Lap ${firstCompare.lap}`,
        legend: `Lap ${firstCompare.lap} · first valid`,
        sectors: sectorsOf(firstCompare),
      };
    }
    if (compare === 'record' && recordCompare) {
      return { id: recordCompare.id, label: 'Record', legend: 'Lap record', sectors: sectorsOf(recordCompare) };
    }
    return null;
  }, [compare, firstCompare, recordCompare]);

  useEffect(() => {
    if (loadState === 'ready' && lapId) ensureTelemetry(lapId);
  }, [loadState, lapId, ensureTelemetry]);
  useEffect(() => {
    if (compareTarget) ensureTelemetry(compareTarget.id);
  }, [compareTarget, ensureTelemetry]);

  // The comparison goes under REF_LAP_ID: the charts draw it dashed and plot the delta against it.
  const selectedTelemetry = lapId ? telemetry.get(lapId) : undefined;
  const compareTelemetry = compareTarget ? telemetry.get(compareTarget.id) : undefined;
  const chartLapIds = useMemo(
    () => (lapId ? (compareTelemetry ? [REF_LAP_ID, lapId] : [lapId]) : []),
    [lapId, compareTelemetry],
  );
  // One colour order for charts and legend alike: the comparison is gold (REF_LAP_ID), the viewed
  // lap the "last" colour, red, whether or not a comparison is on.
  const colorOrder = useMemo(() => (lapId ? [REF_LAP_ID, lapId] : []), [lapId]);
  const chartData = useMemo(() => {
    const map = new Map<string, TelemetryLapData>();
    if (lapId && selectedTelemetry) map.set(lapId, selectedTelemetry);
    if (compareTelemetry) map.set(REF_LAP_ID, compareTelemetry);
    return map;
  }, [lapId, selectedTelemetry, compareTelemetry]);
  const lapLabels = useMemo(() => {
    const labels = new Map<string, string>();
    if (selectedLap && lapId) labels.set(lapId, `Lap ${selectedLap.lap}`);
    if (compareTarget) labels.set(REF_LAP_ID, compareTarget.label);
    return labels;
  }, [selectedLap, lapId, compareTarget]);
  const sectorDistances = useMemo(
    () => (selectedLap && selectedTelemetry ? computeSectorDistances(selectedLap, selectedTelemetry) : null),
    [selectedLap, selectedTelemetry],
  );
  const ignoreChartClick = useCallback(() => {}, []);

  const getShareUrl = useCallback(() => shareableLapUrl(window.location.href), []);

  const header = (
    <header className="pl-header">
      <div className="pl-header-inner">
        <LogoLink className="pl-logo" height={48} />
        <div className="pl-header-actions">
          <CopyLinkButton getUrl={getShareUrl} />
          {user
            ? <Link to="/dashboard" className="pl-btn pl-btn-solid">My dashboard</Link>
            : <Link to="/login" className="pl-btn pl-btn-solid">Sign in</Link>}
        </div>
      </div>
    </header>
  );

  if (loadState !== 'ready' || !selectedLap) {
    const message =
      loadState === 'loading' ? 'Loading lap…'
        : loadState === 'notFound' ? "We couldn't find this lap. Check the link and try again."
          : loadState === 'rateLimited' ? 'Too many requests right now. Wait a minute, then try again.'
            : "We couldn't load this lap.";
    return (
      <div className="public-lap">
        {header}
        <main className="pl-main">
          <OfferBanner offerId={offerId} />
          <div className={`pl-state pl-state-${loadState}`} role={loadState === 'loading' ? 'status' : 'alert'}>
            <p>{message}</p>
            {(loadState === 'error' || loadState === 'rateLimited') && (
              <button type="button" className="pl-btn pl-btn-outline" onClick={() => setReloadKey((k) => k + 1)}>
                Try again
              </button>
            )}
          </div>
        </main>
      </div>
    );
  }

  const isBest = !!best && best.id === selectedLap.id;
  const venue = venueDisplayName(selectedLap.location);
  const meta = [selectedLap.driverName, venue, formatLapDate(selectedLap.date)].filter(Boolean).join(' · ');
  const trackName = selectedLap.layout && !selectedLap.track.includes(selectedLap.layout)
    ? `${selectedLap.track} ${selectedLap.layout}`
    : selectedLap.track;
  const sortedLaps = [...laps].sort((a, b) => a.lap - b.lap);
  const selectedColor = getLapColor(selectedLap.id, colorOrder);
  const lapSectors = sectorsOf(selectedLap);
  const hasSectors = lapSectors.some((s) => s > 0);
  const telemetryFailed = failedTelemetry.has(selectedLap.id);

  return (
    <div className="public-lap">
      {header}
      <main className="pl-main">
        <OfferBanner offerId={offerId} />

        <div className="pl-heading-row">
          <div>
            {meta && <div className="pl-meta">{meta}</div>}
            <h1 className="pl-title">Lap {selectedLap.lap} &middot; {formatLapTime(selectedLap.time)}</h1>
            <div className="pl-chips">
              {trackName && <span className="pl-chip">{trackName}</span>}
              {selectedLap.car && <span className="pl-chip">{selectedLap.car}</span>}
              {isBest && <span className="pl-chip pl-chip-best">Best of session</span>}
              {!selectedLap.valid && <span className="pl-chip pl-chip-invalid">Invalid lap</span>}
            </div>
          </div>
          <label className="pl-compare">
            Compare with
            <select value={compare} onChange={(e) => setCompareChoice(e.target.value as CompareKey)}>
              {firstCompare && (
                <option value="first">
                  First valid lap (lap {firstCompare.lap}, {formatLapTime(firstCompare.time)})
                </option>
              )}
              {recordCompare && (
                <option value="record">
                  Lap record ({formatLapTime(recordCompare.time)}{recordCompare.location ? `, ${venueDisplayName(recordCompare.location)}` : ''})
                </option>
              )}
              <option value="none">No comparison</option>
            </select>
          </label>
        </div>

        <div className="pl-body">
          <section className="pl-card pl-charts" aria-label="Speed, throttle and brake">
            <div className="pl-card-head">
              <h2 className="pl-h2">Speed, throttle &amp; brake</h2>
              <div className="pl-legend">
                <span><span className="pl-swatch" style={{ background: selectedColor }} />Lap {selectedLap.lap}</span>
                {compareTarget && compareTelemetry && (
                  <span><span className="pl-swatch pl-swatch-dashed" style={{ borderColor: getLapColor(REF_LAP_ID, colorOrder) }} />{compareTarget.legend}</span>
                )}
              </div>
            </div>
            {selectedTelemetry ? (
              <TelemetryCharts
                key={selectedLap.id}
                lapDataMap={chartData}
                checkedLapIds={chartLapIds}
                baselineLapId={selectedLap.id}
                channels={channelsFor(!!compareTelemetry)}
                trackLength={selectedTelemetry.l}
                onChartClick={ignoreChartClick}
                lapLabels={lapLabels}
                allLapIds={colorOrder}
                sectorDistances={sectorDistances}
                arrowKeyNavigation={false}
                turns={turns}
              />
            ) : (
              <p className="pl-muted pl-chart-state">
                {telemetryFailed ? 'No telemetry was recorded for this lap.' : 'Loading telemetry…'}
              </p>
            )}
          </section>

          <aside className="pl-side">
            <section className="pl-card" aria-labelledby="pl-sectors">
              <h2 id="pl-sectors" className="pl-h2">Sectors</h2>
              {hasSectors ? (
                <table className="pl-table">
                  <thead>
                    <tr>
                      <th>Sector</th>
                      <th className="num">Lap {selectedLap.lap}</th>
                      {compareTarget && <th className="num">vs {compareTarget.label.toLowerCase()}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {lapSectors.map((s, i) => {
                      const other = compareTarget?.sectors[i] ?? 0;
                      const diff = s > 0 && other > 0 ? s - other : null;
                      return (
                        <tr key={i}>
                          <td>S{i + 1}</td>
                          <td className="num">{s > 0 ? formatLapTime(s) : '—'}</td>
                          {compareTarget && (
                            <td className={`num${diff !== null && diff < 0 ? ' pl-gain' : ' pl-muted'}`}>
                              {diff !== null ? formatSignedDelta(diff) : '—'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="pl-muted">No sector times for this lap.</p>
              )}
            </section>

            <section className="pl-card" aria-labelledby="pl-laps">
              <h2 id="pl-laps" className="pl-h2">All {laps.length} {laps.length === 1 ? 'lap' : 'laps'}</h2>
              <div className="pl-lap-row pl-lap-head" aria-hidden="true">
                <span>Lap</span><span className="num">Time</span><span className="num">Gap</span>
              </div>
              <ol className="pl-lap-list">
                {sortedLaps.map((l) => {
                  const counted = isCountedLap(l);
                  const gap = !counted ? 'Invalid'
                    : best && l.id === best.id ? 'Best'
                      : best ? `+${formatLapTime(l.time - best.time)}` : '';
                  const current = l.id === selectedLap.id;
                  const timeText = l.time > 0 ? formatLapTime(l.time) : 'no time';
                  return (
                    <li key={l.id}>
                      <Link
                        to={{ pathname: `/lap/${l.raceId || raceId}/${l.id}`, search }}
                        aria-label={`Lap ${l.lap}, ${timeText}${gap ? `, ${gap}` : ''}`}
                        className={`pl-lap-row${current ? ' is-current' : ''}${counted ? '' : ' is-invalid'}${best && l.id === best.id ? ' is-best' : ''}`}
                        aria-current={current ? 'page' : undefined}
                      >
                        <span>Lap {l.lap}</span>
                        <span className="num">{l.time > 0 ? formatLapTime(l.time) : '—'}</span>
                        <span className="num pl-muted">{gap}</span>
                      </Link>
                    </li>
                  );
                })}
              </ol>
              <p className="pl-note">Invalid laps (out lap, track limits) aren't ranked or compared.</p>
            </section>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default PublicLapPage;
